import type { ClientOrdersResponse, PortfolioOrderStatus } from "@repo/shared/types";
import { Clock, Info, TriangleAlert } from "lucide-react";
import { type ReactNode, useState } from "react";
import { OrderFlowGroup } from "@/components/orders/order-flow-group";
import { OrderListToolbar } from "@/components/orders/order-list-toolbar";
import { OrderStatusBadge } from "@/components/orders/order-status-badge";
import { OrderTypeBadge } from "@/components/orders/order-type-badge";
import { EmptyState } from "@/components/ui/empty-state";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useUserLanguage } from "@/lib/i18n/context";
import { useT } from "@/lib/i18n/use-t";
import { useRegionalFormat } from "@/lib/regional-format/context";
import { cn } from "@/lib/utils";
import { type ClientOrderRow, OrderDetailDialog } from "./order-detail-dialog";
import { formatUnits, formatWholeAmount, getOrderProductName } from "./order-product-name";
import { T } from "./orders-section.i18n";

// A cell value with an explanation on hover or keyboard focus: the app tooltip,
// and a dotted underline so it is visible that there is one. No hint renders
// the value as is.
function CellHint({ hint, children }: { hint: string | null; children: ReactNode }) {
  if (!hint) return <>{children}</>;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className="cursor-help underline decoration-dotted underline-offset-4 rounded-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          {children}
        </button>
      </TooltipTrigger>
      <TooltipContent>{hint}</TooltipContent>
    </Tooltip>
  );
}

/** Executed differs from target (partial fill / price drift) — compared in
 * the order's own mode, at the precision the table shows. An amount-based
 * switch is never flagged: its target is an advice-time estimate of the
 * proceeds, and a sale that fetches a different amount is simply executed. */
function executionDeviates(order: ClientOrderRow): boolean {
  if (order.orderAmountType === "units") {
    return order.executedUnits != null && order.executedUnits !== order.targetUnits;
  }
  if (order.orderType === "switch") return false;
  if (order.executedAmount == null || order.targetAmount == null) return false;
  return Math.round(order.executedAmount) !== Math.round(order.targetAmount);
}

type Props = {
  clientId: string;
  data: ClientOrdersResponse;
  onMutate: () => void;
};

export function OrdersSection({ data, onMutate }: Props) {
  const { formatNumber } = useRegionalFormat();
  const { userLanguage } = useUserLanguage();
  const t = useT(T);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [expandedFlows, setExpandedFlows] = useState<Set<string>>(new Set());
  const [detailOrder, setDetailOrder] = useState<ClientOrderRow | null>(null);

  const toggleFlow = (id: string) => {
    setExpandedFlows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Filter orders and remove empty groups
  const filteredFlows = data.flowInstances
    .map((flow) => ({
      ...flow,
      goals: flow.goals
        .map((goal) => ({
          ...goal,
          portfolios: goal.portfolios
            .map((portfolio) => ({
              ...portfolio,
              orders:
                statusFilter === "all"
                  ? portfolio.orders
                  : portfolio.orders.filter((o) => o.status === statusFilter),
            }))
            .filter((p) => p.orders.length > 0),
        }))
        .filter((g) => g.portfolios.length > 0),
    }))
    .filter((f) => f.goals.length > 0);

  const totalOrders = data.flowInstances.reduce(
    (sum, f) =>
      sum +
      f.goals.reduce((gs, g) => gs + g.portfolios.reduce((ps, p) => ps + p.orders.length, 0), 0),
    0,
  );

  if (totalOrders === 0) {
    return <EmptyState>{t.noOrders}</EmptyState>;
  }

  return (
    <div className="space-y-4">
      <OrderListToolbar
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        onRefresh={onMutate}
        language={userLanguage}
      />

      {filteredFlows.length === 0 ? (
        <p className="text-sm text-muted-foreground py-8 text-center">{t.noOrdersFilter}</p>
      ) : (
        <div className="space-y-3">
          {filteredFlows.map((flow) => {
            const flowExpanded = expandedFlows.has(flow.flowInstanceId);
            const flowOrderCount = flow.goals.reduce(
              (gs, g) => gs + g.portfolios.reduce((ps, p) => ps + p.orders.length, 0),
              0,
            );

            return (
              <OrderFlowGroup
                key={flow.flowInstanceId}
                flowName={flow.flowName}
                completedAt={flow.completedAt}
                summary={`${flowOrderCount} ${flowOrderCount !== 1 ? t.orders : t.order}`}
                expanded={flowExpanded}
                onToggle={() => toggleFlow(flow.flowInstanceId)}
                language={userLanguage}
              >
                {/* goals → portfolios → orders table */}
                {flow.goals.map((goal) => (
                  <div key={goal.goalId} className="space-y-4">
                    {/* Goal heading */}
                    <div className="flex items-center gap-2">
                      <h4 className="text-sm font-medium">{goal.goalName}</h4>
                      <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded">
                        {goal.goalType}
                      </span>
                    </div>

                    {/* Portfolios under this goal */}
                    {goal.portfolios.map((portfolio) => (
                      <div key={portfolio.goalPortfolioId ?? "none"} className="ml-4 space-y-2">
                        {/* Portfolio label */}
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-muted-foreground">
                            {portfolio.portfolioName}
                          </span>
                          <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded">
                            {portfolio.portfolioType}
                          </span>
                        </div>

                        {/* Orders table — slim row; full detail in the ⓘ dialog */}
                        <Table variant="data" surface={false}>
                          <TableHeader>
                            <TableRow>
                              <TableHead>{t.product}</TableHead>
                              <TableHead className="text-right w-[110px]">{t.target}</TableHead>
                              <TableHead className="text-right w-[110px]">{t.executed}</TableHead>
                              <TableHead className="w-[70px]">{t.type}</TableHead>
                              <TableHead className="w-[110px]">{t.status}</TableHead>
                              <TableHead className="w-[40px]" />
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {portfolio.orders.map((order) => {
                              const deviates = executionDeviates(order);
                              // QL-547 — a switch is a percent instruction, so the
                              // amount shown for it is an estimate of the proceeds.
                              const estimatedTarget =
                                order.orderType === "switch" && order.switchPercent != null;
                              // A units-mode switch shows only the units of the fund
                              // being sold; the fund's name and the proceeds go in
                              // the tooltip, so a long name cannot break the column.
                              const unitsSwitch =
                                order.orderType === "switch" && order.orderAmountType === "units";
                              const soldFund = getOrderProductName(
                                order.productName,
                                userLanguage,
                                order.isin,
                              );
                              const money = (n: number | null) =>
                                n != null ? formatWholeAmount(n, formatNumber) : null;
                              const targetMoney = money(order.targetAmount);
                              const executedMoney = money(order.executedAmount);
                              return (
                                <TableRow key={order.id}>
                                  <TableCell className="font-medium">
                                    {/* QL-544 — a switch leads with the fund being bought;
                                        the fund being sold sits underneath. */}
                                    {order.orderType === "switch" ? (
                                      <>
                                        <div>
                                          {getOrderProductName(
                                            order.targetProductName,
                                            userLanguage,
                                            order.targetIsin,
                                          )}
                                        </div>
                                        <div className="text-xs font-normal text-muted-foreground">
                                          {t.switchFrom(
                                            getOrderProductName(
                                              order.productName,
                                              userLanguage,
                                              order.isin,
                                            ),
                                          )}
                                        </div>
                                      </>
                                    ) : (
                                      getOrderProductName(
                                        order.productName,
                                        userLanguage,
                                        order.isin,
                                      )
                                    )}
                                  </TableCell>
                                  <TableCell className="text-right">
                                    <CellHint
                                      hint={
                                        estimatedTarget
                                          ? t.estimatedProceeds(String(order.switchPercent))
                                          : unitsSwitch && order.targetUnits != null
                                            ? [
                                                t.switchUnitsToSell(soldFund),
                                                targetMoney &&
                                                  t.estimatedProceedsAmount(targetMoney),
                                              ]
                                                .filter(Boolean)
                                                .join(" · ")
                                            : null
                                      }
                                    >
                                      {order.orderAmountType === "units"
                                        ? order.targetUnits != null
                                          ? formatUnits(order.targetUnits, userLanguage)
                                          : "—"
                                        : order.targetAmount != null
                                          ? `${estimatedTarget ? `${t.estimatedShort} ` : ""}${formatWholeAmount(
                                              order.targetAmount,
                                              formatNumber,
                                            )}`
                                          : "—"}
                                    </CellHint>
                                  </TableCell>
                                  <TableCell
                                    className={cn(
                                      "text-right",
                                      deviates && "text-warning font-medium",
                                    )}
                                  >
                                    <CellHint
                                      hint={
                                        [
                                          // QL-544 — a switch's Executed is the sale, not
                                          // the amount invested in the fund shown first.
                                          // No hint on the dash before it has executed.
                                          order.orderType !== "switch"
                                            ? null
                                            : unitsSwitch
                                              ? order.executedUnits != null
                                                ? [
                                                    t.switchUnitsSold(soldFund),
                                                    executedMoney &&
                                                      t.proceedsAmount(executedMoney),
                                                  ]
                                                    .filter(Boolean)
                                                    .join(" · ")
                                                : null
                                              : order.executedAmount != null
                                                ? t.proceedsFromSale
                                                : null,
                                          deviates ? t.deviatesFromTarget : null,
                                        ]
                                          .filter(Boolean)
                                          .join(" · ") || null
                                      }
                                    >
                                      {order.orderAmountType === "units"
                                        ? order.executedUnits != null
                                          ? formatUnits(order.executedUnits, userLanguage)
                                          : "—"
                                        : order.executedAmount != null
                                          ? formatWholeAmount(order.executedAmount, formatNumber)
                                          : "—"}
                                    </CellHint>
                                  </TableCell>
                                  <TableCell>
                                    <OrderTypeBadge
                                      orderType={order.orderType}
                                      language={userLanguage}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <span className="inline-flex items-center gap-1.5">
                                      <OrderStatusBadge
                                        status={order.status as PortfolioOrderStatus}
                                        language={userLanguage}
                                      />
                                      {/* QL-283 — a partially executed SWITCH is a normal
                                              resting state (the bank retries the buy side for
                                              days): show an in-progress clock, not a warning. */}
                                      {order.orderType === "switch" &&
                                      order.status === "partially_executed" ? (
                                        <Clock
                                          className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
                                          aria-label={order.statusReason ?? t.switchInProgress}
                                        >
                                          <title>{order.statusReason ?? t.switchInProgress}</title>
                                        </Clock>
                                      ) : (
                                        /* A failure reason is the headline, not detail —
                                               surface it on the row, full text in the dialog */
                                        order.statusReason &&
                                        ["failed", "cancelled", "partially_executed"].includes(
                                          order.status,
                                        ) && (
                                          <TriangleAlert
                                            className="h-3.5 w-3.5 shrink-0 text-warning"
                                            aria-label={order.statusReason}
                                          >
                                            <title>{order.statusReason}</title>
                                          </TriangleAlert>
                                        )
                                      )}
                                    </span>
                                  </TableCell>
                                  <TableCell className="text-right">
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <button
                                          type="button"
                                          className="p-1 rounded hover:bg-muted"
                                          aria-label={t.viewDetails}
                                          onClick={() => setDetailOrder(order)}
                                        >
                                          <Info className="h-4 w-4 text-muted-foreground" />
                                        </button>
                                      </TooltipTrigger>
                                      <TooltipContent>{t.viewDetails}</TooltipContent>
                                    </Tooltip>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </div>
                    ))}
                  </div>
                ))}
              </OrderFlowGroup>
            );
          })}
        </div>
      )}

      <OrderDetailDialog order={detailOrder} onClose={() => setDetailOrder(null)} />
    </div>
  );
}
